import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { pageSearchPatch } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import { userFilters } from './user.filters';
import { USER_TABS } from './user.search';

// Segurança nunca é automatizada pelo agente (U24): as telas levam `agent: false`, nenhuma ação tem
// `page: true`, e as ações com efeito só rodam pela tela montada (diálogo humano).
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela do operador para executar esta ação.`);
};
export const USER_PATH = '/seguranca/usuarios';
export const userPath = (id: string) => `${USER_PATH}/${encodeURIComponent(id)}`;
/** Tamanho inicial das listas do legado (`localStorage.rowsPerPage` ou 5). */
export const USER_DEFAULT_SIZE = 5;

const idInput = z.object({ id: z.string().min(1), name: z.string().optional() });
const groupInput = z.object({ id: z.string().min(1), groupName: z.string().min(1) });

export const userCreate = action({
  id: 'user.create',
  kind: 'draft',
  label: msg`Novo operador`,
  description: msg`Abre o formulário em branco de operador. Nada é gravado até a pessoa salvar.`,
  permission: can('User', 'Create'),
  run: ({ nav }) => nav.to(userPath('novo')),
});
export const userOpen = action({
  id: 'user.open',
  kind: 'navigate',
  row: true,
  label: msg`Editar`,
  description: msg`Abre a ficha de um operador da lista. Nada muda só por abrir.`,
  input: idInput,
  run: ({ nav, input }) => nav.to(userPath(input.id)),
});
export const userFilter = action({
  id: 'user.filter',
  kind: 'navigate',
  label: msg`Filtrar a lista`,
  description: msg`Filtra os operadores pelo começo do nome ou do e-mail.`,
  input: userFilters.inputSchema,
  run: ({ nav, input }) => nav.search({ ...userFilters.patch(input), paginationToken: undefined }),
});
export const userListPageInput = z.object({
  page: z.number().int().min(0),
  size: z.number().int().min(1).max(100),
  paginationToken: z.string().optional(),
});
/** A página N leva o `paginationToken` da resposta exibida, como o `onChangePage` do legado. */
export const userListPage = action({
  id: 'user.list.page',
  kind: 'navigate',
  label: msg`Mudar de página`,
  description: msg`Muda a página (começa em 0) ou o tamanho da página da lista de operadores.`,
  input: userListPageInput,
  run: ({ nav, input }) =>
    nav.search({
      ...pageSearchPatch(input, { defaultSize: USER_DEFAULT_SIZE }),
      paginationToken: input.paginationToken,
    }),
});
export const userDisable = action({
  id: 'user.disable',
  kind: 'destructive',
  row: true,
  label: msg`Desabilitar`,
  description: msg`Desabilita a conta de um operador: ele deixa de conseguir entrar. Pede a confirmação da pessoa.`,
  permission: can('User', 'DisableUser', { strict: true }),
  input: idInput,
  preview: (input) => ({
    title: t`Desabilitar ${input.name ?? input.id}`,
    detail: t`Tem certeza que deseja desabilitar esse operador?`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});
export const userEnable = action({
  id: 'user.enable',
  kind: 'write',
  label: msg`Habilitar usuário`,
  description: msg`Habilita de novo a conta de um operador desabilitado. Pede a confirmação da pessoa.`,
  permission: can('User', 'EnableUser', { strict: true }),
  input: idInput,
  preview: () => ({
    title: t`Habilitar usuário`,
    detail: t`Tem certeza que deseja habilitar esse usuário?`,
  }),
  run: mountedScreenRequired,
});
export const userSave = action({
  id: 'user.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Salva o formulário do operador aberto.`,
  input: idInput,
  preview: (input) => ({ title: t`Salvar operador`, detail: input.name ?? input.id }),
  run: mountedScreenRequired,
});
export const userRefresh = action({
  id: 'user.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados do operador aberto. Não muda nada.`,
  input: idInput,
  run: mountedScreenRequired,
});
export const userResetMfa = action({
  id: 'user.resetMfa',
  kind: 'write',
  label: msg`Resetar MFA`,
  description: msg`Desassocia os métodos de autenticação multifator do operador, que precisará configurá-los de novo. Pede a confirmação da pessoa.`,
  input: idInput,
  preview: () => ({
    title: t`Resetar MFA`,
    detail: t`Tem certeza que deseja resetar o MFA desse usuário? Isso desassociará métodos de autenticação multifator e precisará ser refeito.`,
  }),
  run: mountedScreenRequired,
});
export const userResetPassword = action({
  id: 'user.resetPassword',
  kind: 'write',
  label: msg`Resetar senha`,
  description: msg`Gera uma nova senha para o operador e a envia para o e-mail cadastrado. Pede a confirmação da pessoa.`,
  input: idInput,
  preview: () => ({
    title: t`Resetar senha`,
    detail: t`Tem certeza que deseja resetar a senha desse usuário? Uma nova senha será enviada automaticamente para o e-mail cadastrado.`,
  }),
  run: mountedScreenRequired,
});
export const userHistory = action({
  id: 'user.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o histórico de alterações do operador. Não muda nada.`,
  input: idInput,
  run: mountedScreenRequired,
});
export const userTab = action({
  id: 'user.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba da ficha do operador (Informações, Grupos ou Permissões).`,
  input: z.object({ tab: z.enum(USER_TABS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab }),
});
export const userGroupAdd = action({
  id: 'user.groups.add',
  kind: 'write',
  label: msg`Adicionar a grupo`,
  description: msg`Inclui o operador num grupo de usuários que já existe; as permissões do grupo passam a valer para ele. Pede a confirmação da pessoa.`,
  permission: can('UserGroup', 'Update'),
  input: groupInput,
  preview: (input) => ({ title: t`Adicionar a grupo`, detail: input.groupName }),
  run: mountedScreenRequired,
});
export const userGroupRemove = action({
  id: 'user.groups.remove',
  kind: 'destructive',
  row: true,
  label: msg`Excluir`,
  description: msg`Tira o operador de um grupo de usuários. Pede a confirmação da pessoa.`,
  permission: can('UserGroup', 'Delete'),
  input: groupInput,
  preview: () => ({
    title: t`Excluir`,
    detail: t`Tem certeza que deseja excluir esse registro?`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});
export const userGroupOpen = action({
  id: 'user.groups.open',
  kind: 'navigate',
  row: true,
  label: msg`Visualizar`,
  description: msg`Abre a ficha de um grupo do operador. Nada muda só por abrir.`,
  permission: can('UserGroup', 'Read'),
  input: groupInput,
  run: mountedScreenRequired,
});
export const userGroupsRefresh = action({
  id: 'user.groups.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os grupos do operador. Não muda nada.`,
  input: idInput,
  run: mountedScreenRequired,
});
export const userGroupsPage = action({
  id: 'user.groups.page',
  kind: 'navigate',
  label: msg`Mudar de página`,
  description: msg`Muda quantos grupos do operador aparecem na lista.`,
  input: z.object({ page: z.number().int().min(0), size: z.number().int().min(1).max(100) }),
  run: mountedScreenRequired,
});
// Aba Permissões: o editor comum (F1c) não registra ferramenta; declarado aqui só para o manifesto (U24).
export const userPermissionsSave = action({
  id: 'user.permissions.save',
  kind: 'write',
  label: msg`Salvar permissões`,
  description: msg`Grava de uma vez as alterações de permissões do operador, depois da revisão do antes e do depois. Pede a confirmação da pessoa.`,
  permission: can('Permission', 'Update'),
  input: idInput,
  preview: () => ({ title: t`Salvar permissões` }),
  run: mountedScreenRequired,
});
export const userPermissionsClear = action({
  id: 'user.permissions.clear',
  kind: 'destructive',
  label: msg`Limpar permissões`,
  description: msg`Remove todas as permissões do operador. Pede a confirmação da pessoa.`,
  permission: can('Permission', 'Delete'),
  input: idInput,
  preview: () => ({
    title: t`Limpar permissões`,
    detail: t`Remover todas as permissões do operador?`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});
export const userPermissionsCopy = action({
  id: 'user.permissions.copy',
  kind: 'write',
  label: msg`Copiar permissões`,
  description: msg`Copia para este operador as permissões de outro operador, depois de mostrar a prévia do que muda. Pede a confirmação da pessoa.`,
  permission: can('Permission', 'Create'),
  input: idInput,
  preview: () => ({ title: t`Copiar permissões` }),
  run: mountedScreenRequired,
});

export const userListScreen = defineScreen({
  id: 'user.list',
  path: USER_PATH,
  title: msg`Usuários`,
  description: msg`Lista dos operadores (usuários do sistema), com filtros por nome e e-mail.`,
  permission: can('User', 'Read'),
  place: { icon: 'shield-check', section: 'security' },
  agent: false,
  actions: [userCreate, userFilter, userListPage, userOpen, userDisable],
});
export const userDetailScreen = defineScreen({
  id: 'user.detail',
  path: `${USER_PATH}/$userId`,
  title: msg`Operador`,
  description: msg`Ficha do operador: informações, grupos e permissões.`,
  permission: can('User', 'Read'),
  place: {
    icon: 'user',
    parent: 'user.list',
    record: msg`Operador`,
    tabs: [
      { id: 'informacoes', label: msg`Informações` },
      { id: 'grupos', label: msg`Grupos` },
      { id: 'permissoes', label: msg`Permissões` },
    ],
  },
  agent: false,
  actions: [
    userSave,
    userRefresh,
    userResetMfa,
    userResetPassword,
    userDisable,
    userEnable,
    userHistory,
    userTab,
    userGroupAdd,
    userGroupRemove,
    userGroupOpen,
    userGroupsRefresh,
    userGroupsPage,
    userPermissionsSave,
    userPermissionsClear,
    userPermissionsCopy,
  ],
});
